// Smart Daara - Password Reset Dialog
// Shows a confirmation, then displays the new password (visible only once) with copy button.
import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { userAPI } from '../../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { KeyRound, Copy, Check, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable password-reset modal.
 * Props:
 *  - open (bool)
 *  - onOpenChange (fn)
 *  - user: { id, first_name, last_name, email } — required
 *  - userId (optional): overrides `user.id` (used for teacher/parent -> underlying user_id)
 */
export default function PasswordResetDialog({ open, onOpenChange, user, userId }) {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const t = (fr, en) => (language === 'fr' ? fr : en);
  const targetId = userId || user?.id;
  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

  const handleReset = async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const res = await userAPI.resetPassword(targetId);
      setNewPassword(res.data.new_password);
      toast.success(t('Mot de passe réinitialisé', 'Password reset'));
    } catch (e) {
      const detail = e.response?.data?.detail || t('Erreur', 'Error');
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(t('Copié !', 'Copied!'));
    } catch (e) {
      toast.error(t('Impossible de copier', 'Cannot copy'));
    }
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) {
      // Reset state on close so next open starts fresh
      setNewPassword('');
      setCopied(false);
      setLoading(false);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="password-reset-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-600" />
            {t('Réinitialiser le mot de passe', 'Reset password')}
          </DialogTitle>
          <DialogDescription>
            {newPassword
              ? t(
                  "Un nouveau mot de passe a été généré. Communiquez-le à l'utilisateur : il ne sera plus visible après fermeture.",
                  'A new password has been generated. Share it with the user — it will not be shown again.'
                )
              : t(
                  `Un nouveau mot de passe aléatoire va être généré pour ${fullName || (user?.email || '')}. L'ancien mot de passe sera invalidé.`,
                  `A random new password will be generated for ${fullName || (user?.email || '')}. The old password will be invalidated.`
                )}
          </DialogDescription>
        </DialogHeader>

        {!newPassword ? (
          <div className="py-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                {t(
                  "Assurez-vous que l'utilisateur est informé. Le mot de passe ne sera affiché qu'une seule fois.",
                  'Make sure the user is informed. The password will only be shown once.'
                )}
              </p>
            </div>
          </div>
        ) : (
          <div className="py-4 space-y-3">
            <div className="text-xs text-slate-500">
              {t('Utilisateur', 'User')} : <span className="font-medium text-slate-700">{user?.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="flex-1 font-mono text-lg tracking-wider bg-slate-100 border border-slate-200 rounded-lg px-4 py-3 select-all"
                data-testid="new-password-display"
              >
                {newPassword}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopy}
                data-testid="copy-password-btn"
                title={t('Copier', 'Copy')}
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              {t(
                "Astuce : demandez à l'utilisateur de changer ce mot de passe à sa prochaine connexion.",
                'Tip: ask the user to change this password on their next login.'
              )}
            </p>
          </div>
        )}

        <DialogFooter>
          {!newPassword ? (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={loading}>
                {t('Annuler', 'Cancel')}
              </Button>
              <Button
                onClick={handleReset}
                disabled={loading || !targetId}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-reset-password-btn"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <KeyRound className="w-4 h-4 mr-2" />
                {t('Générer un nouveau mot de passe', 'Generate new password')}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleClose(false)}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="close-reset-password-btn"
            >
              {t("J'ai bien noté", 'Got it')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
